// Plan 23b, Task 2.1 — Floating Popup primitive
// Behavior contract: screen-space popup that rises + fades via direct GSAP.
// Injectable textPopHook for 3D scene-space side effects only.

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type FloatingPopupKind =
  | 'key-gain'
  | 'key-loss'
  | 'hp-damage'
  | 'hp-heal'
  | 'trophy-gain'
  | 'item-received';

export interface FloatingPopupTrigger {
  /** Display text, e.g. "+5" or "-10". */
  text: string;
  /** Visual style variant — determines color. */
  kind: FloatingPopupKind;
  /** Screen-space X coordinate (pixels from left). */
  screenX: number;
  /** Screen-space Y coordinate (pixels from top). */
  screenY: number;
}

/**
 * Optional hook for 3D scene-space side effects.
 * The actual DOM animation is driven by direct GSAP — NOT by this hook.
 * The hook receives the popup HTMLElement but may ignore it (Plan 20a's
 * createTextPopHook is a no-op DOM hook that delegates to ComicVFXManager).
 */
export interface FloatingPopupHook {
  play(el: HTMLElement, opts?: unknown): GsapTimelineHandle;
}

export interface FloatingPopupOpts {
  /**
   * Optional injectable hook for 3D scene-space side effects (e.g. Plan 20a
   * createTextPopHook). If omitted, the 3D side effect is skipped.
   * The DOM animation is always driven by direct GSAP regardless.
   */
  textPopHook?: FloatingPopupHook;
  /** Total visible lifetime before removal. Defaults to the original 1s popup. */
  lifetimeSeconds?: number;
  /** Vertical gap between simultaneous popups at the same anchor. Defaults to 44px. */
  stackGapPx?: number;
  /** Maximum simultaneous popups at one anchor before queuing. Defaults to 3. */
  maxVisiblePerAnchor?: number;
  /**
   * Stack direction for simultaneous popups at one anchor. Defaults to the
   * original behavior: the oldest popup stays at the anchor and newer popups
   * appear below it.
   */
  stackOrder?: 'oldest-at-anchor' | 'newest-at-anchor';
  /**
   * Behavior when more than maxVisiblePerAnchor popups target one anchor.
   * Defaults to queueing overflow until an active popup completes.
   */
  overflowMode?: 'queue' | 'drop-oldest';
  /** Screen-space bucket size for grouping nearby anchors. Defaults to 64px. */
  anchorBucketPx?: number;
  /** Called when any popup is active or queued, and again when the layer is idle. */
  onActivityChange?: (active: boolean) => void;
}

export interface FloatingPopupHandle extends UIComponentHandle {
  /** Fire a floating popup at the given screen position. */
  trigger(opts: FloatingPopupTrigger): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Design token constants — values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** Color per kind — sourced from tokens.json */
const KIND_COLOR: Record<FloatingPopupKind, string> = {
  'key-gain': '#FFD700', // color.hud.key-gold
  'key-loss': '#E04040', // color.text.danger
  'hp-damage': '#E04040', // color.text.danger
  'hp-heal': '#5CAD40', // color.text.success
  'trophy-gain': '#FFD700', // color.hud.trophy-gold
  'item-received': '#A060D8', // color.tile.item
};

/**
 * typography.family.headline = 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif
 */
const FONT_FAMILY =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";

/** typography.size.h3 = 28px */
const FONT_SIZE = 'var(--typography-size-h3, 28px)';

/**
 * typography.shadow.heading — fallback: "0 2px 8px rgba(0,0,0,0.70)"
 * Actual token: "0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.60)"
 * Spec fallback: "0 2px 8px rgba(0,0,0,0.70)" — used as the var default.
 */
const TEXT_SHADOW = 'var(--typography-shadow-heading, 0 2px 8px rgba(0,0,0,0.70))';

/** animation.easing.bouncy — cubic-bezier from tokens.json */
const BOUNCY_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** animation.duration.dramatic = 1000ms = 1.0s in GSAP */
const DRAMATIC_DURATION_S = 1.0;

/** Entrance scale duration: 200ms = 0.2s */
const ENTRANCE_DURATION_S = 0.2;

/** Rise distance: translateY 0 → -80px */
const RISE_PX = -80;

/** Opacity fade starts at 60% of dramatic duration = 600ms = 0.6s */
const FADE_START_S = 0.6;

/** Fade duration: from 0.6s to 1.0s = 0.4s */
const FADE_DURATION_S = DRAMATIC_DURATION_S - FADE_START_S;

interface ActivePopup {
  readonly element: HTMLElement;
  readonly handle: GsapTimelineHandle;
}

interface AnchorState {
  readonly anchorX: number;
  readonly anchorY: number;
  readonly active: ActivePopup[];
  readonly queued: FloatingPopupTrigger[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createFloatingPopup(
  config: UIComponentConfig & FloatingPopupOpts,
): FloatingPopupHandle {
  const { gsapRegistry, mount, textPopHook } = config;
  const lifetimeSeconds = Math.max(
    config.lifetimeSeconds ?? DRAMATIC_DURATION_S,
    ENTRANCE_DURATION_S,
  );
  const fadeStartSeconds = Math.max(ENTRANCE_DURATION_S, lifetimeSeconds - FADE_DURATION_S);
  const stackGapPx = Math.max(0, config.stackGapPx ?? 44);
  const maxVisiblePerAnchor = Math.max(1, Math.floor(config.maxVisiblePerAnchor ?? 3));
  const stackOrder = config.stackOrder ?? 'oldest-at-anchor';
  const overflowMode = config.overflowMode ?? 'queue';
  const anchorBucketPx = Math.max(1, config.anchorBucketPx ?? 64);

  let disposed = false;
  let triggerCount = 0;
  let lastActivity = false;

  /** Tracks in-flight popups and overflow queues by nearby screen-space anchor. */
  const anchorStates = new Map<string, AnchorState>();

  // ── Root container element (passed back as handle.element) ─────────────────
  // This is a zero-size absolute-positioned container. Individual popup
  // elements are created per trigger() call and appended into mount or
  // this container.
  const container = document.createElement('div');
  container.className = 'ui-floating-popup-host';
  container.style.cssText = [
    'position: absolute',
    'top: 0',
    'left: 0',
    'width: 0',
    'height: 0',
    'overflow: visible',
    'pointer-events: none',
  ].join('; ');

  if (mount) {
    mount.appendChild(container);
  }

  // ── trigger ───────────────────────────────────────────────────────────────

  function anchorKeyFor(opts: FloatingPopupTrigger): string {
    const bucketX = Math.round(opts.screenX / anchorBucketPx);
    const bucketY = Math.round(opts.screenY / anchorBucketPx);
    return `${bucketX}:${bucketY}`;
  }

  function getAnchorState(opts: FloatingPopupTrigger): AnchorState {
    const key = anchorKeyFor(opts);
    const existing = anchorStates.get(key);
    if (existing) return existing;
    const state: AnchorState = {
      anchorX: opts.screenX,
      anchorY: opts.screenY,
      active: [],
      queued: [],
    };
    anchorStates.set(key, state);
    return state;
  }

  function reflowAnchor(state: AnchorState): void {
    state.active.forEach((entry, index) => {
      const offset =
        stackOrder === 'newest-at-anchor'
          ? -(state.active.length - 1 - index) * stackGapPx
          : index * stackGapPx;
      entry.element.style.top = `${state.anchorY + offset}px`;
    });
  }

  function cleanupAnchorState(state: AnchorState): void {
    if (state.active.length > 0 || state.queued.length > 0) return;
    for (const [key, value] of anchorStates) {
      if (value === state) {
        anchorStates.delete(key);
        break;
      }
    }
  }

  function hasPopupActivity(): boolean {
    for (const state of anchorStates.values()) {
      if (state.active.length > 0 || state.queued.length > 0) return true;
    }
    return false;
  }

  function syncActivity(): void {
    const active = hasPopupActivity();
    if (active === lastActivity) return;
    lastActivity = active;
    config.onActivityChange?.(active);
  }

  function flushAnchorQueue(state: AnchorState): void {
    while (!disposed && state.active.length < maxVisiblePerAnchor && state.queued.length > 0) {
      const next = state.queued.shift();
      if (next == null) break;
      showPopup(state, next);
    }
    cleanupAnchorState(state);
    syncActivity();
  }

  function showPopup(state: AnchorState, opts: FloatingPopupTrigger): void {
    const { text, kind } = opts;
    const n = triggerCount++;

    // ── Create popup element ──────────────────────────────────────────────

    const popupEl = document.createElement('div');
    popupEl.setAttribute('data-floating-popup', kind);
    popupEl.style.cssText = [
      // Screen-space position
      'position: absolute',
      `left: ${state.anchorX}px`,
      `top: ${state.anchorY}px`,
      // Typography — typography.family.headline, typography.size.h3, bold
      `font-family: ${FONT_FAMILY}`,
      `font-size: ${FONT_SIZE}`,
      'font-weight: 700',
      // Color per kind — explicit hex for test assertions + CSS var for browsers
      `color: ${KIND_COLOR[kind]}`,
      // Text shadow — typography.shadow.heading
      `text-shadow: ${TEXT_SHADOW}`,
      // Layout
      'white-space: nowrap',
      'pointer-events: none',
      'user-select: none',
      // Start fully opaque
      'opacity: 1',
      // Transform origin for scale entrance
      'transform-origin: center bottom',
    ].join('; ');

    // Override color with CSS var form for correct variable resolution in browsers.
    // Keep explicit hex already set for test assertion compatibility (happy-dom
    // resolves the computed style from the inline hex, not the var).
    popupEl.style.color = KIND_COLOR[kind];

    popupEl.textContent = text;

    // Append to mount (or container if no mount)
    const parent = mount ?? container;
    parent.appendChild(popupEl);

    // ── GSAP timeline — F20a-CLASS-1: all tl.* calls have explicit position ──

    const handle = gsapRegistry.create(`floating-popup:trigger-${n}`);
    const tl = handle.timeline;
    const activeEntry: ActivePopup = { element: popupEl, handle };
    state.active.push(activeEntry);
    reflowAnchor(state);
    syncActivity();

    // Entrance: scale 1.3 → 1 over 200ms with bouncy ease, starting at t=0
    // F20a-CLASS-1: explicit position 0 as third argument
    tl.fromTo(
      popupEl,
      { scale: 1.3 },
      { scale: 1, duration: ENTRANCE_DURATION_S, ease: BOUNCY_EASE },
      0, // F20a-CLASS-1
    );

    // Rise: translateY 0 → RISE_PX over DRAMATIC_DURATION_S (1000ms), starting at t=0
    // F20a-CLASS-1: explicit position 0 as third argument
    tl.to(
      popupEl,
      { y: RISE_PX, duration: lifetimeSeconds },
      0, // F20a-CLASS-1
    );

    // Fade: opacity 1 → 0 starting at FADE_START_S (600ms) over FADE_DURATION_S (400ms)
    // F20a-CLASS-1: explicit position FADE_START_S as third argument
    tl.fromTo(
      popupEl,
      { opacity: 1 },
      { opacity: 0, duration: FADE_DURATION_S },
      fadeStartSeconds, // F20a-CLASS-1
    );

    // Remove from DOM at t=DRAMATIC_DURATION_S (1.0s)
    // Use tl.call(fn, undefined, position) — NOT eventCallback('onComplete')
    // The sentinel tween at VIRTUAL_DURATION=3600s means onComplete fires at 3600s.
    // F20a-CLASS-1: explicit position 1.0 as third argument
    function removeEl(): void {
      if (popupEl.parentNode) {
        popupEl.parentNode.removeChild(popupEl);
      }
      removeActiveEntry(state, activeEntry);
      handle.dispose();
      if (state.queued.length > 0) {
        queueMicrotask(() => flushAnchorQueue(state));
      } else {
        cleanupAnchorState(state);
      }
      syncActivity();
    }

    tl.call(removeEl, undefined, lifetimeSeconds); // F20a-CLASS-1

    // ── Optional 3D side-effect hook ──────────────────────────────────────
    // Call only if provided; the DOM animation above is always direct GSAP.
    if (textPopHook) {
      textPopHook.play(popupEl, opts);
    }
  }

  function removeActiveEntry(state: AnchorState, entry: ActivePopup): void {
    const idx = state.active.indexOf(entry);
    if (idx !== -1) {
      state.active.splice(idx, 1);
    }
    reflowAnchor(state);
  }

  function dropOldestActivePopup(state: AnchorState): void {
    const oldest = state.active[0];
    if (oldest == null) return;
    removeActiveEntry(state, oldest);
    oldest.handle.dispose();
    if (oldest.element.parentNode) {
      oldest.element.parentNode.removeChild(oldest.element);
    }
  }

  function trigger(opts: FloatingPopupTrigger): void {
    if (disposed) return;

    const state = getAnchorState(opts);
    if (state.active.length >= maxVisiblePerAnchor) {
      if (overflowMode === 'drop-oldest') {
        dropOldestActivePopup(state);
        showPopup(state, opts);
        return;
      }
      state.queued.push(opts);
      syncActivity();
      return;
    }

    showPopup(state, opts);
  }

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const state of anchorStates.values()) {
      for (const entry of state.active) {
        entry.handle.dispose();
        if (entry.element.parentNode) {
          entry.element.parentNode.removeChild(entry.element);
        }
      }
      state.active.length = 0;
      state.queued.length = 0;
    }
    anchorStates.clear();
    syncActivity();

    // Remove any remaining popup elements from their parent.
    // They may still be in mount or container if their removeEl hasn't fired yet.
    const parent = mount ?? container;
    const popups = parent.querySelectorAll('[data-floating-popup]');
    for (const el of popups) {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    }

    // Detach container from DOM
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  return {
    element: container,
    trigger,
    dispose,
  };
}
